// CartContext.tsx
import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import { CART_ITEMS, COUNT_CART_ITEMS } from '../lib/graphql/queries/customer';
import {
  ADD_TO_CART,
  CLEAR_CART,
  REMOVE_FROM_CART,
  UPDATE_CART_ITEM,
} from '../lib/graphql/mutations/customer';

// Shape from your backend
interface Product {
  id: string;
  name: string;
  thumbnail: string | null;
  price: number;
  discountPrice: number | null;
}

interface CartItemFromServer {
  id: string;
  productId: string;
  quantity: number;
  product: Product;
}

export interface CartItem {
  id: string; // cart item ID (not product ID)
  productId: string;
  name: string;
  price: number; // final price (discountPrice ?? price)
  thumbnail: string | null;
  quantity: number;
}

interface CartContextType {
  items: CartItem[];
  addToCart: (productId: string, quantity?: number) => Promise<void>;
  removeFromCart: (cartItemId: string) => Promise<void>;
  updateQuantity: (cartItemId: string, quantity: number) => Promise<void>;
  clearCart: () => Promise<void>;
  total: number;
  itemCount: number;
  loading: boolean;
  refetch: () => Promise<any>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const client = useApolloClient();

  // Fetch cart items
  const { data, loading, refetch } = useQuery(CART_ITEMS, {
    fetchPolicy: 'network-only', // always get fresh cart
    onError: (err) => console.error('Failed to load cart:', err),
  });

  // Get cart item count separately (optional badge)
  const { data: countData } = useQuery(COUNT_CART_ITEMS, {
    pollInterval: 30_000, // optional: keep count fresh
  });

  const [addMutation] = useMutation(ADD_TO_CART);
  const [removeMutation] = useMutation(REMOVE_FROM_CART);
  const [updateMutation] = useMutation(UPDATE_CART_ITEM);
  const [clearMutation] = useMutation(CLEAR_CART);

  // Transform server data → local format
  const items: CartItem[] = (data?.cartItems || []).map(
    (item: CartItemFromServer): CartItem => ({
      id: item.id,
      productId: item.productId,
      name: item.product.name,
      price: item.product.discountPrice ?? item.product.price,
      thumbnail: item.product.thumbnail,
      quantity: item.quantity,
    })
  );

  const itemCount = countData?.cartItemCount || items.length;
  const total = items.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  // Optimistic UI + refetch on completion
  const addToCart = async (productId: string, quantity = 1) => {
    await addMutation({
      variables: { productId, quantity },
      // Optimistic response
      optimisticResponse: {
        customerAddCartItem: {
          __typename: 'CartItem',
          id: 'temp-' + Math.random(), // temporary ID
        },
      },
      // Better: refetch cart after mutation
      refetchQueries: [{ query: CART_ITEMS }, { query: COUNT_CART_ITEMS }],
    });
  };

  const removeFromCart = async (cartItemId: string) => {
    await removeMutation({
      variables: { customerRemoveCartItemId: cartItemId },
      refetchQueries: [{ query: CART_ITEMS }, { query: COUNT_CART_ITEMS }],
    });
  };

  const updateQuantity = async (cartItemId: string, quantity: number) => {
    if (quantity <= 0) {
      await removeFromCart(cartItemId);
      return;
    }

    await updateMutation({
      variables: { customerUpdateCartItemId: cartItemId, quantity },
      refetchQueries: [{ query: CART_ITEMS }, { query: COUNT_CART_ITEMS }],
    });
  };

  const clearCart = async () => {
    await clearMutation({
      refetchQueries: [{ query: CART_ITEMS }, { query: COUNT_CART_ITEMS }],
    });
  };

  // Optional: refetch cart when window regains focus (e.g. after checkout on another tab)
  useEffect(() => {
    const handleFocus = () => refetch();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refetch]);

  const value: CartContextType = {
    items,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    total,
    itemCount,
    loading,
    refetch,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
